/**
 * Component for simple table with reorderable rows.
 */
import {StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

import ReorderableList from './ReorderableList';
import {Cell, CELL_SHAPE, Row, ROW_SHAPE} from './ReorderableSimpleTableConstants';
import ReorderableSimpleTableRow, {ReorderableSimpleTableRowProps} from './ReorderableSimpleTableRow';

interface ReorderableSimpleTableProps {
  headerCells: Array<Cell>;
  /** A row at the bottom of the table that can't be reordered */
  footerCells?: Array<Cell>;
  isDraggingDisabled: boolean;
  noRowsDisplay: string | JSX.Element;
  // Called with row ID, new index when reordering is complete
  onReorderRow?: (rowId: string, newIndex: number) => void;
  rows: Array<Row>;
  dataTestAttrs?: DataTestAttrs;
}

interface State {
  currentlyOrderedRows: Array<Row>;
}

export default class ReorderableSimpleTable extends React.Component<ReorderableSimpleTableProps, State> {
  static propTypes = {
    headerCells: PropTypes.arrayOf(CELL_SHAPE.isRequired).isRequired,
    footerCells: PropTypes.arrayOf(CELL_SHAPE.isRequired),
    isDraggingDisabled: PropTypes.bool.isRequired,
    noRowsDisplay: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    // Called with row ID, new index when reordering is complete
    onReorderRow: PropTypes.func,
    rows: PropTypes.arrayOf(ROW_SHAPE.isRequired).isRequired,
    dataTestAttrs: DataTestAttrsType,
  };

  override state: State = {
    currentlyOrderedRows: this.props.rows,
  };

  override UNSAFE_componentWillReceiveProps(nextProps: ReorderableSimpleTableProps): void {
    if (nextProps.rows !== this.props.rows) {
      this.setState({
        currentlyOrderedRows: nextProps.rows,
      });
    }
  }

  _handleMoveItem = (newOrderedIds: Array<string>): void => {
    const rowsById = _.indexBy(this.state.currentlyOrderedRows, 'id');
    this.setState({currentlyOrderedRows: newOrderedIds.map((id) => rowsById[id])});
  };

  _handleMoveItemEnd = (rowId: string): void => {
    // eslint-disable-next-line no-restricted-syntax
    const currentIndex = _.findIndex(this.state.currentlyOrderedRows, (row) => row.id === rowId);
    if (this.props.onReorderRow) {
      this.props.onReorderRow(rowId, currentIndex);
    }
  };

  _getRowDatas(): Array<{
    id: string;
    itemProps: ReorderableSimpleTableRowProps;
    itemClass: React.ComponentType<ReorderableSimpleTableRowProps>;
  }> {
    return this.state.currentlyOrderedRows.map((row) => ({
      id: row.id,
      itemProps: {
        row,
        rowClassName: row.rowClassName,
        isDraggingDisabled: this.props.isDraggingDisabled,
      },
      itemClass: ReorderableSimpleTableRow,
    }));
  }

  override render(): JSX.Element {
    return (
      <div className="metadata-table simpleDivTable" {...getTestProps(this.props.dataTestAttrs)}>
        <div className="simpleDivTable-header">
          {this.props.headerCells.map((cell) => (
            <div key={cell.key} className={classNames('simpleDivTable-headerCell', cell.cellClassName)}>
              <div className={classNames('u-small-spacing-bottom', cell.contentsClassName)}>
                {cell.contents}
              </div>
            </div>
          ))}
        </div>
        {this.props.rows.length ? (
          <ReorderableList
            itemDatas={this._getRowDatas()}
            onMoveItem={this._handleMoveItem}
            onMoveItemEnd={this._handleMoveItemEnd}
          />
        ) : (
          <div className="simpleDivTable-row">
            {this.props.noRowsDisplay && typeof this.props.noRowsDisplay === 'string' ? (
              <div className="simpleDivTable-cell simpleDivTable-cell--emptyMessage">
                <span className="u-faded">{this.props.noRowsDisplay}</span>
              </div>
            ) : (
              <div className="simpleDivTable-cell">{this.props.noRowsDisplay}</div>
            )}
          </div>
        )}
        {this.props.footerCells && (
          <div className="simpleDivTable-footer">
            {this.props.footerCells.map((cell) => (
              <div key={cell.key} className={classNames('simpleDivTable-cell', cell.cellClassName)}>
                <div className={cell.contentsClassName}>{cell.contents}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
export const reorderableSimpleTableStyles = StyleSheet.create({
  wideColumn: {
    flex: 6,
  },
  wrapText: {
    lineHeight: 'normal',
    whiteSpace: 'normal',
  },
});
